/**
 * Unified tool section: a single "Thinking" / "N tool calls" header above
 * all tool-call cards across every chain-of-thought group in a message.
 *
 * - {@link ToolSectionProvider} wraps the message body and owns the
 *   shared expand/collapse state.
 * - {@link UnifiedToolHeader} reads all message parts and renders one
 *   header — "Thinking" shimmer while running, "N tool calls" when done.
 * - {@link ChainOfThought} renders only its group's tool cards (no
 *   per-group header), hidden when the shared state is collapsed.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
} from "react";
import { ChainOfThoughtPrimitive, useAuiState } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import { DefaultToolMessage } from "~/components/assistant-v2/DefaultToolMessage";
import { ShimmerText } from "~/components/assistant/ShimmerText";
import { cn } from "~/lib/utils";

// ---------------------------------------------------------------------------
// Shared collapse context
// ---------------------------------------------------------------------------

const ToolSectionContext = createContext<{
  collapsed: boolean;
  toggle: () => void;
}>({ collapsed: false, toggle: () => {} });

// Tools that show user-facing status after resolution — keep section expanded
// so the user sees the outcome rather than having it immediately collapse away.
const SHOW_RESULT_TOOLS = new Set([
  "pipeline_submit_run",
  "pipeline_cancel_run",
  "cohort_explorer_get_cohort",
]);

export const ToolSectionProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed((v) => !v), []);

  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const hasUnresolvedToolCalls = useAuiState((s) =>
    (s.message.parts ?? []).some(
      (p: { type: string; toolName?: string; result?: unknown }) =>
        p.type === "tool-call" &&
        (p.result === undefined || SHOW_RESULT_TOOLS.has(p.toolName ?? ""))
    )
  );

  // Auto-expand while running or awaiting human input; auto-collapse when done.
  useEffect(() => {
    setCollapsed(!isRunning && !hasUnresolvedToolCalls);
  }, [isRunning, hasUnresolvedToolCalls]);

  return (
    <ToolSectionContext.Provider value={{ collapsed, toggle }}>
      {children}
    </ToolSectionContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Single header for the entire message
// ---------------------------------------------------------------------------

export const UnifiedToolHeader: FC = () => {
  const { collapsed, toggle } = useContext(ToolSectionContext);
  const isRunning = useAuiState((s) => s.message.status?.type === "running");
  const parts = useAuiState((s) => s.message.parts);

  const toolCount = useMemo(
    () => (parts ?? []).filter((p: { type: string }) => p.type === "tool-call").length,
    [parts]
  );
  const hasReasoning = useMemo(
    () => (parts ?? []).some((p: { type: string }) => p.type === "reasoning"),
    [parts]
  );

  const latestReasoning = useMemo(() => {
    const reasoning = (parts ?? []).filter((p: { type: string }) => p.type === "reasoning");
    const last = reasoning[reasoning.length - 1] as { type: "reasoning"; text: string } | undefined;
    if (!last) return null;
    return last.text.split("\u001e").pop()?.trim() || null;
  }, [parts]);

  // Nothing to show yet
  if (toolCount === 0 && !hasReasoning) return null;
  // Reasoning-only and no longer running — nothing useful to display
  if (toolCount === 0 && !isRunning) return null;

  return (
    <div className="pb-2">
      <div
        role="button"
        tabIndex={0}
        className="flex items-center cursor-pointer select-none w-fit gap-1"
        onClick={toggle}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle()}
      >
        {isRunning ? (
          <ShimmerText className="text-sm font-medium">Thinking</ShimmerText>
        ) : (
          <span className="text-sm font-medium text-purple-600 pb-1">
            {toolCount} tool call{toolCount !== 1 ? "s" : ""}
          </span>
        )}
        <ChevronRight
          className={cn(
            "size-3.5 transition-transform duration-200",
            isRunning ? "text-gray-400" : "text-purple-600",
            !collapsed && "rotate-90"
          )}
        />
      </div>

      {!collapsed && isRunning && latestReasoning && (
        <p className="text-sm leading-relaxed text-gray-500 mt-1.5 mb-3">{latestReasoning}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-group renderer (tool cards only, no header)
// ---------------------------------------------------------------------------

/** Reasoning parts render as nothing — latest reasoning is in UnifiedToolHeader. */
const CoTReasoningPart: FC = () => null;

export const ChainOfThought: FC = () => {
  const { parts } = useAuiState((s) => s.chainOfThought);
  const { collapsed } = useContext(ToolSectionContext);

  const toolCount = parts.filter((p) => p.type === "tool-call").length;

  // Nothing to render for reasoning-only groups or when collapsed
  if (toolCount === 0 || collapsed) return null;

  return (
    <ChainOfThoughtPrimitive.Root>
      <div className="space-y-2">
        <ChainOfThoughtPrimitive.Parts
          components={{
            Reasoning: CoTReasoningPart,
            tools: { Fallback: DefaultToolMessage },
          }}
        />
      </div>
    </ChainOfThoughtPrimitive.Root>
  );
};
